'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion } from 'framer-motion'

const futureCategories = [
  {
    id: 'wine',
    name: 'Wine & Spirits',
    description: 'Fine wine, rare whisky, and collectible spirits',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3h6l1 7c0 3-2 5-4 5s-4-2-4-5l1-7zM12 15v6M8 21h8" />
      </svg>
    ),
    stats: '$8.2B market',
    color: '#722F37',
  },
  {
    id: 'watches',
    name: 'Watches',
    description: 'Luxury timepieces from Patek to Rolex',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6l4 2" />
      </svg>
    ),
    stats: '$24B market',
    color: '#1C1C1C',
  },
  {
    id: 'couture',
    name: 'Couture & Fashion',
    description: 'Vintage fashion, handbags, and designer pieces',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 2l3 4h6l3-4M9 6v16M15 6v16M9 12h6M9 18h6" />
      </svg>
    ),
    stats: '$12B market',
    color: '#C9A227',
  },
  {
    id: 'collectibles',
    name: 'Toys & Collectibles',
    description: 'Trading cards, vintage toys, memorabilia',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth={1.5} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 9h.01M15 9h.01M9 15h6" />
      </svg>
    ),
    stats: '$30B market',
    color: '#E63946',
  },
]

export default function AboutPage() {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [email, setEmail] = useState('')

  const toggleCategory = (id: string) => {
    setSelectedCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    )
  }

  return (
    <main className="min-h-screen">
      {/* Hero with Color Field */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Rothko-style color fields */}
        <div className="absolute inset-0">
          {/* Ground color */}
          <div className="absolute inset-0" style={{ background: 'var(--hero-ground)' }} />

          {/* Top field - warm accent */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="absolute top-0 left-0 right-0 h-[55%]"
            style={{
              background: 'linear-gradient(180deg, var(--accent-primary) 0%, var(--accent-warm) 60%, transparent 100%)',
              filter: 'blur(40px)',
              opacity: 0.6,
            }}
          />

          {/* Bottom field - secondary accent */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 0.3 }}
            className="absolute bottom-0 left-0 right-0 h-[40%]"
            style={{
              background: 'linear-gradient(0deg, var(--accent-secondary) 0%, var(--accent-deep) 50%, transparent 100%)',
              filter: 'blur(60px)',
              opacity: 0.5,
            }}
          />

          {/* Subtle texture overlay */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 w-full px-6 py-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm font-medium uppercase tracking-wider mb-6"
              style={{ color: 'var(--accent-cream)' }}
            >
              Our Story
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-8 leading-tight"
              style={{ color: 'var(--accent-cream)' }}
            >
              The intelligence layer
              <span className="block mt-2">for serious market participants</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl max-w-2xl mx-auto leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              We build AI-powered market intelligence for alternative assets,
              starting with fine art.
            </motion.p>
          </div>
        </div>
      </section>

      {/* The Story */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-xl text-text-secondary leading-relaxed mb-6">
              The art market has always rewarded those with better information.
              The best dealers have it. The top advisors have it. The auction houses certainly have it.
            </p>
            <p className="text-xl text-text-secondary leading-relaxed mb-6">
              We built salle.art for the professionals who understand that in a market defined by information asymmetry,
              the quality of your intelligence determines the quality of your decisions.
              <strong className="text-text-primary"> Not data. Intelligence.</strong>
            </p>
            <p className="text-xl text-text-secondary leading-relaxed">
              Our AI synthesizes 50 years of market history—and every upcoming lot—into insights that matter: pricing confidence, timing signals, comparable analysis before the hammer falls.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The AI-Native Advantage */}
      <section className="py-20 px-6 bg-card">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-light mb-4">
              Built different. Run leaner.
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              We're not a traditional data company with hundreds of analysts manually
              cataloging auction results. We're an AI-native team of domain experts
              who build systems that scale.
            </p>
          </motion.div>

          {/* Key stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-6 rounded-2xl bg-page"
            >
              <p className="text-4xl font-display font-light mb-1" style={{ color: 'var(--accent-primary)' }}>85M</p>
              <p className="text-xs text-text-tertiary">Lots analyzed</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center p-6 rounded-2xl bg-page"
            >
              <p className="text-4xl font-display font-light mb-1" style={{ color: 'var(--accent-primary)' }}>180K</p>
              <p className="text-xs text-text-tertiary">Artists tracked</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center p-6 rounded-2xl bg-page"
            >
              <p className="text-4xl font-display font-light mb-1" style={{ color: 'var(--accent-primary)' }}>50+</p>
              <p className="text-xs text-text-tertiary">Years of data</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center p-6 rounded-2xl bg-page"
            >
              <p className="text-4xl font-display font-light mb-1" style={{ color: 'var(--accent-primary)' }}>0</p>
              <p className="text-xs text-text-tertiary">Manual data entry</p>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-text-tertiary text-sm mt-8"
          >
            A small team of domain experts in art, data science, and technology.
            AI handles the scale. Humans ensure the quality.
          </motion.p>
        </div>
      </section>

      {/* The Vision - Fine Art is Just the Start */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-light mb-6">
            Fine art is just the beginning
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            The same intelligence architecture that understands Basquiat can understand Burgundy.
            We're building the definitive market intelligence layer for alternative assets.
          </p>
        </div>

        {/* Future categories */}
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {futureCategories.map((cat, i) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="card p-6 text-center relative overflow-hidden group"
            >
              <div className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: cat.color }} />
              <div
                className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center transition-colors"
                style={{ background: `${cat.color}15`, color: cat.color }}
              >
                {cat.icon}
              </div>
              <h3 className="font-medium mb-1">{cat.name}</h3>
              <p className="text-xs text-text-tertiary mb-2">{cat.description}</p>
              <p className="text-xs font-medium" style={{ color: cat.color }}>{cat.stats}</p>
              <span className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full bg-page text-text-tertiary">
                Coming soon
              </span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Interest Capture - While You're Here */}
      <section className="py-20 px-6 bg-gradient-to-br from-hero-ground to-accent-secondary">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-light mb-4" style={{ color: 'var(--accent-cream)' }}>
              Shape what comes next
            </h2>
            <p className="text-lg" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Tell us which markets you'd like to see.
              Early supporters get priority access.
            </p>
          </div>

          {/* Category selection */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {futureCategories.map((cat) => {
              const isSelected = selectedCategories.includes(cat.id)
              return (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    isSelected
                      ? 'bg-white/10 border-white/30'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-white border-white' : 'border-white/40'
                      }`}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3 text-hero-ground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--accent-cream)' }}>
                      {cat.name}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Email capture */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-white/40"
            />
            <button
              disabled={!email.includes('@') || selectedCategories.length === 0}
              className="px-6 py-3 rounded-xl font-medium transition-all disabled:opacity-50"
              style={{ background: 'var(--accent-cream)', color: 'var(--hero-ground)' }}
            >
              Notify me
            </button>
          </div>
          <p className="text-center text-xs mt-4" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {selectedCategories.length > 0
              ? `You'll be first to know when ${selectedCategories.length === 1 ? 'this category launches' : 'these categories launch'}.`
              : 'Select at least one category above.'}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-card">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl font-light mb-4">
            Start with fine art today
          </h2>
          <p className="text-text-secondary mb-8 max-w-xl mx-auto">
            Experience what institutional-grade market intelligence can do for your decisions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              href="/get-started"
              className="pill"
              style={{ background: 'var(--accent-primary)', color: 'white' }}
            >
              Request access
            </Link>
            <Link
              href="/"
              className="pill"
              style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
            >
              Explore the platform
            </Link>
          </div>
        </div>
      </section>
    </main>
  )
}
